// QuestionView.js — Primitives voor het nieuwe toets-render-design
// Geïntegreerd vanuit ToetsDemo (variant A · Minimal). Alle componenten zijn
// scoped binnen .tw-qv-root (zie QuestionView.css) zodat ze de rest van de
// app niet beïnvloeden.

import React, { useState } from 'react';
import VraagTekst from '../components/VraagTekst';
import { MdTekst } from '../components/InhoudBlokken';
import VoorleesTekst from '../components/leesbaarheid/VoorleesTekst';
import { heeftGrafiek } from '../utils/grafiekConfig';

// Lettergroottes en regelafstanden komen uit CSS-variabelen op .tw-qv-root
// (QuestionView.css); de leesinstellingen (dyslexie, 09-10-2026) zetten die
// per leerling anders via src/styles/leesinstellingen.css.
const FS_UI = 'var(--tw-fs-ui, 15px)';
const FS_INPUT = 'var(--tw-fs-input, 17px)';
const LH = 'var(--tw-lh, 1.4)';

// Optietekst: met zinPrefix per zin (voorlezen markeert de zin die klinkt),
// anders de gewone VraagTekst. Nooit klikbaar: de optie zelf is al een knop.
// md: optie uit de CE-vragenbank (migratie 077) met **vet**/*cursief*; alleen
// dan, zodat een '*' in bestaande opties gewoon een sterretje blijft.
function OptieTekst({ tekst, zinPrefix, style, md }) {
  if (md) return <span style={style}><MdTekst md={tekst} /></span>;
  if (zinPrefix) return <VoorleesTekst tekst={tekst} prefix={zinPrefix} klikbaar={false} style={style} />;
  return <VraagTekst tekst={tekst} style={style} />;
}

// ────────────────────────────────────────────────────────────────
// Icon — inline SVG (geen externe assets, werkt in lockdown)
// ────────────────────────────────────────────────────────────────
export function QvIcon({ name, size = 20, stroke = 1.75, style }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: stroke,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    style,
  };
  switch (name) {
    case 'lock':
      return (<svg {...common}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>);
    case 'shield':
      return (<svg {...common}><path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z"/></svg>);
    case 'clock':
      return (<svg {...common}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>);
    case 'play':
      return (<svg {...common}><path d="M6 4l14 8-14 8V4z" fill="currentColor" stroke="none"/></svg>);
    case 'pause':
      return (<svg {...common}><rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor" stroke="none"/><rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor" stroke="none"/></svg>);
    case 'check':
      return (<svg {...common}><path d="M4 12l5 5L20 6"/></svg>);
    case 'arrow-left':
      return (<svg {...common}><path d="M19 12H5M12 19l-7-7 7-7"/></svg>);
    case 'arrow-right':
      return (<svg {...common}><path d="M5 12h14M12 5l7 7-7 7"/></svg>);
    case 'user':
      return (<svg {...common}><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></svg>);
    case 'volume':
      return (<svg {...common}><path d="M11 5L6 9H3v6h3l5 4V5z"/><path d="M15 9a3 3 0 0 1 0 6"/></svg>);
    case 'zoom':
      return (<svg {...common}><circle cx="11" cy="11" r="7"/><path d="M21 21l-5-5"/><path d="M11 8v6M8 11h6"/></svg>);
    case 'rekenmachine':
      return (<svg {...common}><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M8 6h8"/><path d="M8 11h.01M12 11h.01M16 11h.01M8 15h.01M12 15h.01M16 15h.01M8 19h.01M12 19h4"/></svg>);
    case 'formulekaart':
      return (<svg {...common}><path d="M4 4h16v16H4z"/><path d="M8 8h3M9.5 6.5v3"/><path d="M13 8h3"/><path d="M8 15h3"/><path d="M13 14h3M13 16h3"/></svg>);
    case 'arrow-up':
      return (<svg {...common}><path d="M12 5v14M5 12l7-7 7 7"/></svg>);
    case 'arrow-down':
      return (<svg {...common}><path d="M12 5v14M5 12l7 7 7-7"/></svg>);
    default:
      return null;
  }
}

// ────────────────────────────────────────────────────────────────
// QvButton — primary/submit/secondary/ghost
// ────────────────────────────────────────────────────────────────
export function QvButton({ variant = 'primary', onClick, children, disabled, style, ariaLabel, type = 'button' }) {
  const base = {
    height: 44,
    padding: '0 18px',
    borderRadius: 10,
    font: '500 15px/1 var(--tw-font-sans)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    cursor: disabled ? 'not-allowed' : 'pointer',
    border: '1px solid transparent',
    transition: 'background var(--tw-duration) var(--tw-ease), border-color var(--tw-duration) var(--tw-ease), color var(--tw-duration) var(--tw-ease), transform 80ms var(--tw-ease)',
    opacity: disabled ? 0.5 : 1,
    userSelect: 'none',
  };
  const skin = {
    primary:   { background: 'var(--tw-primary)',  color: '#fff' },
    submit:    { background: 'var(--tw-success)',  color: '#fff' },
    secondary: { background: 'var(--tw-surface)',  color: 'var(--tw-text)', borderColor: 'var(--tw-border)' },
    ghost:     { background: 'transparent',        color: 'var(--tw-primary-hover)' },
    warning:   { background: 'var(--tw-amber)',    color: '#fff' },
  }[variant];
  return (
    <button
      type={type}
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      style={{ ...base, ...skin, ...style }}
      onMouseDown={(e) => { if (!disabled) e.currentTarget.style.transform = 'scale(0.98)'; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = ''; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ''; }}
    >
      {children}
    </button>
  );
}

// ────────────────────────────────────────────────────────────────
// QvBadge — status pill (amber/teal/green/blue/muted)
// ────────────────────────────────────────────────────────────────
export function QvBadge({ tone = 'muted', children }) {
  const tones = {
    amber: { bg: 'rgba(245,158,11,.12)', fg: '#B45309' },
    teal:  { bg: 'rgba(13,148,136,.12)', fg: 'var(--tw-primary-hover)' },
    green: { bg: 'rgba(16,185,129,.12)', fg: '#047857' },
    blue:  { bg: 'rgba(0,119,182,.12)',  fg: '#0077b6' },
    muted: { bg: 'var(--tw-surface-muted)', fg: 'var(--tw-text-muted)' },
  }[tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      height: 28, padding: '0 12px', borderRadius: 999,
      fontSize: 13, fontWeight: 500,
      background: tones.bg, color: tones.fg,
    }}>{children}</span>
  );
}

// ────────────────────────────────────────────────────────────────
// QvTimer — escalerende kleur + pulse < 1 min
// ────────────────────────────────────────────────────────────────
export function QvTimer({ seconds }) {
  const safe = Math.max(0, seconds || 0);
  const mm = String(Math.floor(safe / 60)).padStart(2, '0');
  const ss = String(safe % 60).padStart(2, '0');
  let color = 'var(--tw-text)';
  let pulse = false;
  if (safe <= 60) { color = 'var(--tw-timer-op)'; pulse = true; }
  else if (safe <= 300) { color = 'var(--tw-timer-bijna)'; }
  else if (safe <= 600) { color = 'var(--tw-timer-let-op)'; }
  return (
    <div role="timer" aria-label={`${mm} minuten ${ss} seconden resterend`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontFamily: 'var(--tw-font-mono)', fontVariantNumeric: 'tabular-nums',
        fontSize: 17, fontWeight: 600, letterSpacing: '-0.02em',
        color, animation: pulse ? 'tw-pulse 1s ease-in-out infinite' : 'none',
      }}>
      <QvIcon name="clock" size={16} />
      {mm}:{ss}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// QvExtraTijd — chip naast de timer: deze leerling heeft extra tijd.
// Zelfde rustige teal-tint als de andere badges; geen gradient. Op smal scherm
// valt het woord "extra tijd" weg (alleen "+25%"); de volledige zin blijft in
// title en aria-label.
// ────────────────────────────────────────────────────────────────
export function QvExtraTijd({ procent }) {
  const zin = `Je krijgt ${procent}% extra tijd`;
  return (
    <span className="tw-qv-extratijd" data-testid="extra-tijd" role="note" title={zin} aria-label={zin}>
      <QvBadge tone="teal">
        <QvIcon name="clock" size={14} stroke={2} />
        <span aria-hidden="true">+{procent}%<span className="tw-qv-extratijd-woord"> extra tijd</span></span>
      </QvBadge>
    </span>
  );
}

// ────────────────────────────────────────────────────────────────
// QvProgressBar — 3px, flush onder header
// ────────────────────────────────────────────────────────────────
export function QvProgressBar({ value, total }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={total}
      style={{ height: 3, background: 'var(--tw-border)', position: 'relative', flexShrink: 0 }}>
      <div style={{
        position: 'absolute', inset: 0, width: `${pct}%`,
        background: 'var(--tw-primary)',
        transition: 'width var(--tw-duration) var(--tw-ease)',
      }} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// QvDotNav — bolletjes voor vraagnavigatie
//   teal=actief, donkergrijs=beantwoord, lichtgrijs=open
// ────────────────────────────────────────────────────────────────
export function QvDotNav({ current, total, answeredSet, onJump }) {
  return (
    <div role="tablist" aria-label="Vraagnavigatie"
      style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
      {Array.from({ length: total }).map((_, i) => {
        const isActive = i === current;
        const isAnswered = answeredSet.has(i);
        return (
          <button
            key={i}
            role="tab"
            aria-selected={isActive}
            aria-label={`Ga naar vraag ${i + 1}`}
            onClick={() => onJump(i)}
            style={{
              width: 10, height: 10, borderRadius: 999,
              padding: 0, border: 'none', cursor: 'pointer',
              background: isActive ? 'var(--tw-primary)'
                : isAnswered ? '#64748b' : '#cbd5e1',
              transform: isActive ? 'scale(1.08)' : 'scale(1)',
              transition: 'background var(--tw-duration) var(--tw-ease), transform var(--tw-duration) var(--tw-ease)',
            }}
          />
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// QvMCOption — card-stijl MC keuze met letter-badge
// Gekozen = dikkere teal rand, lichte achtergrond en gevuld letterbolletje, plus
// aria-checked. Bewust GEEN vinkje (25-10-2026): tijdens het maken leek een ✓
// op "goed" en viel het bij lange tekst over de optie heen. Een vinkje hoort
// alleen bij inzage/nakijken (QvReviewOptions), waar het wel "goed" betekent.
// ────────────────────────────────────────────────────────────────
export function QvMCOption({ letter, label, selected, onSelect, imageUrl, zinPrefix, md }) {
  const [hover, setHover] = useState(false);
  const active = selected;
  const hasImage = !!imageUrl;
  const labelEmpty = !label || !String(label).trim();
  return (
    <button
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: hasImage ? 'flex-start' : 'center',
        flexDirection: hasImage ? 'column' : 'row',
        gap: 12,
        background: (active || hover) ? 'var(--tw-primary-light)' : 'var(--tw-surface)',
        // Gekozen: 2px rand; de padding gaat 1px omlaag zodat de kaart niet verspringt.
        border: `${active ? 2 : 1}px solid ${active || hover ? 'var(--tw-primary)' : 'var(--tw-border)'}`,
        borderRadius: 12,
        padding: hasImage ? (active ? '11px' : '12px') : (active ? '13px 15px' : '14px 16px'),
        minHeight: 60, textAlign: 'left',
        color: 'var(--tw-text)', width: '100%',
        position: 'relative',
        boxShadow: 'var(--tw-card-shadow, 0 1px 3px rgba(0,0,0,.04))',
        transition: 'background var(--tw-duration) var(--tw-ease), border-color var(--tw-duration) var(--tw-ease), transform 80ms var(--tw-ease)',
        fontFamily: 'var(--tw-font-sans)', fontWeight: 400, fontSize: FS_UI, lineHeight: LH,
      }}
      onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.99)'; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = ''; }}
    >
      {hasImage && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
          {letter && (
            <span aria-hidden="true"
              style={{
                flexShrink: 0,
                width: 30, height: 30, borderRadius: 999,
                display: 'grid', placeItems: 'center',
                fontWeight: 600, fontSize: 14,
                background: active ? 'var(--tw-primary)' : 'var(--tw-surface-muted, #f1f3f9)',
                color: active ? '#fff' : 'var(--tw-text-muted)',
              }}>{letter}</span>
          )}
          {!labelEmpty && <OptieTekst tekst={label} zinPrefix={zinPrefix} md={md} style={{ flex: 1, fontWeight: 500 }} />}
        </div>
      )}
      {hasImage && (
        <img src={imageUrl} alt={label || `Optie ${letter}`}
          style={{
            width: '100%', maxHeight: 200, objectFit: 'contain',
            borderRadius: 8, background: '#fff',
          }} />
      )}
      {!hasImage && letter && (
        <span aria-hidden="true"
          style={{
            flexShrink: 0,
            width: 30, height: 30, borderRadius: 999,
            display: 'grid', placeItems: 'center',
            fontWeight: 600, fontSize: 14,
            background: active ? 'var(--tw-primary)' : 'var(--tw-surface-muted, #f1f3f9)',
            color: active ? '#fff' : 'var(--tw-text-muted)',
            transition: 'background var(--tw-duration) var(--tw-ease), color var(--tw-duration) var(--tw-ease)',
          }}>
          {letter}
        </span>
      )}
      {!hasImage && <OptieTekst tekst={label} zinPrefix={zinPrefix} md={md} style={{ flex: 1 }} />}
    </button>
  );
}

// ────────────────────────────────────────────────────────────────
// QvTextInput — single line input (voor fill-in)
// ────────────────────────────────────────────────────────────────
export function QvTextInput({ value, onChange, placeholder, ariaLabel, autoComplete = 'off', inputMode }) {
  return (
    <input
      type="text"
      inputMode={inputMode}
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck="false"
      style={{
        width: '100%', boxSizing: 'border-box',
        background: 'var(--tw-surface)',
        border: '1px solid var(--tw-border)',
        borderRadius: 8, padding: '12px 14px',
        fontFamily: 'var(--tw-font-sans)', fontWeight: 400, fontSize: FS_INPUT, lineHeight: LH,
        color: 'var(--tw-text)',
        transition: 'border-color var(--tw-duration) var(--tw-ease), box-shadow var(--tw-duration) var(--tw-ease)',
      }}
    />
  );
}

// ────────────────────────────────────────────────────────────────
// QvTextarea — multi line textarea (voor open vragen)
// ────────────────────────────────────────────────────────────────
export const QvTextarea = React.forwardRef(function QvTextarea({ value, onChange, placeholder, ariaLabel, rows = 8 }, ref) {
  return (
    <textarea
      ref={ref}
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{
        width: '100%', boxSizing: 'border-box',
        flex: 1, minHeight: 140, resize: 'vertical',
        background: 'var(--tw-surface)',
        border: '1px solid var(--tw-border)',
        borderRadius: 8, padding: '14px 16px',
        fontFamily: 'var(--tw-font-sans)', fontWeight: 400, fontSize: FS_INPUT, lineHeight: 'var(--tw-lh-q, 1.55)',
        color: 'var(--tw-text)',
        transition: 'border-color var(--tw-duration) var(--tw-ease), box-shadow var(--tw-duration) var(--tw-ease)',
      }}
    />
  );
});

// ────────────────────────────────────────────────────────────────
// QvHeader — top bar (titel + badges | vraag X van Y + timer | leerling)
// ────────────────────────────────────────────────────────────────
// rechts: extra knop(pen) rechts in de balk, voor de leerlingnaam (bijv. Leesinstellingen).
// extraTijdProcent: > 0 → rustige teal chip "+25% extra tijd" naast de timer
// (25-10-2026). Stond eerder als paarse zwevende pil linksonder en lag dan over
// de knop Vorige in de voetbalk.
export function QvHeader({ examName, studentName, current, total, seconds, secured, sebActive, rechts, extraTijdProcent = 0 }) {
  return (
    <header style={{
      minHeight: 'var(--tw-header-h, 60px)',
      // Kopbalk op de iPad (25-10-2026): de titel krimpt (ellipsis), het midden
      // en rechts (Leesinstellingen + naam) nooit; vaste tussenruimte, zodat
      // de titel niet meer doorloopt in "Vraag 2 van 3" en de naam niet tot
      // een paar pixels wordt samengedrukt. Smal scherm: zie QuestionView.css.
      display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto minmax(max-content, 1fr)',
      columnGap: 16,
      alignItems: 'center',
      padding: '0 var(--tw-pad-x, 24px)',
      // Safe-area: header-inhoud onder de statusbalk (iPad/WKWebView). Achtergrond vult door.
      paddingTop: 'env(safe-area-inset-top, 0px)',
      background: 'var(--tw-surface)',
      borderBottom: '1px solid var(--tw-border)',
      boxShadow: 'var(--tw-rail-shadow, 0 1px 3px rgba(0,0,0,.04))',
      flexShrink: 0,
    }} className="tw-qv-header">
      <div className="tw-qv-header-links" style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <span className="tw-qv-header-titel" title={examName} style={{
          minWidth: 0,
          fontSize: 'var(--tw-examname-size, 17px)',
          fontWeight: 600, color: 'var(--tw-text)',
          letterSpacing: '-0.005em',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{examName}</span>
        {secured && (
          <QvBadge tone="amber"><QvIcon name="lock" size={14} stroke={2} /> Beveiligd</QvBadge>
        )}
        {sebActive && (
          <QvBadge tone="blue"><QvIcon name="shield" size={14} stroke={2} /> SEB</QvBadge>
        )}
      </div>

      <div className="tw-qv-header-midden" style={{ display: 'flex', alignItems: 'center', gap: 16, whiteSpace: 'nowrap' }}>
        <span style={{
          fontSize: 14, color: 'var(--tw-text-muted)',
          fontFamily: 'var(--tw-font-mono)', fontVariantNumeric: 'tabular-nums',
        }}>
          Vraag <span style={{ color: 'var(--tw-text)', fontWeight: 600 }}>{current + 1}</span> van {total}
        </span>
        {/* seconds == null → inzage/review: geen timer */}
        {seconds != null && <div style={{ width: 1, height: 20, background: 'var(--tw-border)' }} />}
        {seconds != null && <QvTimer seconds={seconds} />}
        {extraTijdProcent > 0 && <QvExtraTijd procent={extraTijdProcent} />}
      </div>

      <div className="tw-qv-header-rechts" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
        {rechts}
        {/* Naam krimpt niet; te lang = ellipsis (max-width in CSS). Op smal
            scherm alleen het icoon; de naam blijft voor schermlezers en in title. */}
        <span className="tw-qv-leerling" title={studentName} data-testid="qv-leerling">
          <QvIcon name="user" size={20} style={{ color: 'var(--tw-text-muted)', flexShrink: 0 }} />
          <span className="tw-qv-leerling-naam">{studentName}</span>
        </span>
      </div>
    </header>
  );
}

// ────────────────────────────────────────────────────────────────
// QvFooter — bottom bar (Vorige | DotNav | Volgende/Inleveren)
// ────────────────────────────────────────────────────────────────
export function QvFooter({ current, total, answeredSet, onJump, onPrev, onNext, isLast, inleverLabel = 'Toets inleveren' }) {
  return (
    <footer style={{
      // Safe-area in de HOOGTE verwerken (niet als padding) zodat de knoppen écht in het
      // midden van de balk staan i.p.v. omhoog geduwd. Achtergrond vult tot de schermrand,
      // home-indicator-clearance komt uit de extra hoogte.
      minHeight: 'calc(var(--tw-footer-h, 76px) + env(safe-area-inset-bottom, 0px))',
      display: 'grid', gridTemplateColumns: '1fr auto 1fr',
      alignItems: 'center',
      padding: '0 var(--tw-pad-x, 24px)',
      background: 'var(--tw-surface)',
      borderTop: '1px solid var(--tw-border)',
      boxShadow: 'var(--tw-rail-shadow-top, 0 -1px 3px rgba(0,0,0,.04))',
      flexShrink: 0,
      zIndex: 50,
    }}>
      <div>
        <QvButton variant="secondary" onClick={onPrev} disabled={current === 0}
          ariaLabel="Ga naar vorige vraag">
          <QvIcon name="arrow-left" size={16} stroke={2} /> Vorige
        </QvButton>
      </div>
      <QvDotNav current={current} total={total} answeredSet={answeredSet} onJump={onJump} />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {isLast ? (
          <QvButton variant="submit" onClick={onNext} ariaLabel={inleverLabel}>
            Inleveren <QvIcon name="check" size={16} stroke={2.25} />
          </QvButton>
        ) : (
          <QvButton variant="primary" onClick={onNext} ariaLabel="Ga naar volgende vraag">
            Volgende <QvIcon name="arrow-right" size={16} stroke={2} />
          </QvButton>
        )}
      </div>
    </footer>
  );
}

// ────────────────────────────────────────────────────────────────
// Review-varianten (inzage/resultaat) — zelfde schil als de toets,
// maar met status-gekleurde genummerde balletjes per vraag:
// groen = alle punten, oranje = deels goed, rood = fout.
// ────────────────────────────────────────────────────────────────
const QV_REVIEW_KLEUR = {
  goed: 'var(--tw-success)',
  deels: 'var(--tw-warning)',
  fout: 'var(--tw-danger)',
};
const QV_REVIEW_LABEL = { goed: 'alle punten', deels: 'deels goed', fout: 'fout', open: 'nagekeken' };

export function QvReviewDots({ current, statuses, onJump }) {
  return (
    <div role="tablist" aria-label="Vraagnavigatie"
      style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
      {statuses.map((status, i) => {
        const isActive = i === current;
        return (
          <button
            key={i}
            role="tab"
            aria-selected={isActive}
            aria-label={`Vraag ${i + 1} — ${QV_REVIEW_LABEL[status] || 'onbekend'}`}
            onClick={() => onJump(i)}
            style={{
              width: 28, height: 28, borderRadius: 999,
              padding: 0, cursor: 'pointer',
              display: 'grid', placeItems: 'center',
              fontSize: 13, fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              background: QV_REVIEW_KLEUR[status] || 'var(--tw-border-strong)',
              color: '#fff',
              border: 'none',
              boxShadow: isActive ? '0 0 0 2px var(--tw-surface), 0 0 0 4px var(--tw-text)' : 'none',
              transform: isActive ? 'scale(1.08)' : 'scale(1)',
              transition: 'transform var(--tw-duration) var(--tw-ease), box-shadow var(--tw-duration) var(--tw-ease)',
            }}
          >
            {i + 1}
          </button>
        );
      })}
    </div>
  );
}

export function QvReviewFooter({ current, total, statuses, onJump, onPrev, onNext, isLast, lastLabel = 'Overzicht', rechts }) {
  return (
    <footer style={{
      minHeight: 'calc(var(--tw-footer-h, 76px) + env(safe-area-inset-bottom, 0px))',
      display: 'grid', gridTemplateColumns: '1fr auto 1fr',
      alignItems: 'center',
      padding: '0 var(--tw-pad-x, 24px)',
      background: 'var(--tw-surface)',
      borderTop: '1px solid var(--tw-border)',
      boxShadow: 'var(--tw-rail-shadow-top, 0 -1px 3px rgba(0,0,0,.04))',
      flexShrink: 0,
      zIndex: 50,
    }}>
      <div>
        <QvButton variant="secondary" onClick={onPrev} disabled={current === 0}
          ariaLabel="Ga naar vorige vraag">
          <QvIcon name="arrow-left" size={16} stroke={2} /> Vorige
        </QvButton>
      </div>
      <QvReviewDots current={current} statuses={statuses} onJump={onJump} />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {rechts != null ? rechts : (
          <QvButton variant="primary" onClick={onNext}
            ariaLabel={isLast ? `Ga naar ${lastLabel.toLowerCase()}` : 'Ga naar volgende vraag'}>
            {isLast ? lastLabel : 'Volgende'} <QvIcon name="arrow-right" size={16} stroke={2} />
          </QvButton>
        )}
      </div>
    </footer>
  );
}

// Read-only MC/TF-opties met markering: groen = juiste antwoord,
// rood = jouw foute keuze; zelfde kaart-stijl als QvMCOption.
// zinPrefix: 'optie' geeft per optie zinnen 'optie-0-0', ... (voorlezen in de inzage).
export function QvReviewOptions({ options, correctIndex, chosenIndex, zinPrefix, md }) {
  return (
    <div role="list" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {options.map((optie, i) => {
        const isJuist = i === correctIndex;
        const isGekozenFout = i === chosenIndex && !isJuist;
        const rand = isJuist ? 'var(--tw-success)' : isGekozenFout ? 'var(--tw-danger)' : 'var(--tw-border)';
        const vlak = isJuist ? 'rgba(16,185,129,0.07)' : isGekozenFout ? 'rgba(239,68,68,0.06)' : 'var(--tw-surface)';
        return (
          <div key={i} role="listitem"
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              background: vlak,
              border: `1px solid ${rand}`,
              borderRadius: 12, padding: '14px 16px', minHeight: 56,
              fontFamily: 'var(--tw-font-sans)', fontWeight: 400, fontSize: FS_UI, lineHeight: LH,
              color: 'var(--tw-text)',
              position: 'relative',
            }}>
            <span aria-hidden="true"
              style={{
                flexShrink: 0, width: 30, height: 30, borderRadius: 999,
                display: 'grid', placeItems: 'center', fontWeight: 600, fontSize: 14,
                background: isJuist ? 'var(--tw-success)' : isGekozenFout ? 'var(--tw-danger)' : 'var(--tw-surface-muted, #f1f3f9)',
                color: (isJuist || isGekozenFout) ? '#fff' : 'var(--tw-text-muted)',
              }}>
              {String.fromCharCode(65 + i)}
            </span>
            <OptieTekst tekst={optie} zinPrefix={zinPrefix ? `${zinPrefix}-${i}` : undefined} md={md} style={{ flex: 1 }} />
            {isJuist && (
              <span style={{ color: 'var(--tw-success)', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <QvIcon name="check" size={15} stroke={2.5} />
                {chosenIndex === i ? 'Jouw antwoord' : 'Juiste antwoord'}
              </span>
            )}
            {isGekozenFout && (
              <span style={{ color: 'var(--tw-danger)', fontSize: 13, fontWeight: 600 }}>✗ Jouw antwoord</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────

// Bepaal of een vraag een afbeelding heeft die een sidebar-layout rechtvaardigt.
// Regel: MC + image → sidebar, alle andere combinaties → media boven.
export function shouldUseSidebarLayout(question) {
  // Een grafiek telt als afbeelding (07-10-2026).
  if (!question?.imageUrl && !heeftGrafiek(question)) return false;
  return question.type === 'mc' || question.type === 'tf';
}
